import React, { useState, useEffect, useRef } from 'react';
import {
  Briefcase, Search, Loader2, MessageSquare, Send, Paperclip, X, Download, Upload,
  FileText, Image, Film, User, Shield, ArrowLeft, Clock, Phone, Mail,
  Copy, Check, XCircle, ChevronRight, AlertTriangle
} from 'lucide-react';
import { servicesService } from '../../services/ServicesService';

const POLL_INTERVAL = 5000;

const AdminServicesPage = () => {
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', service_type: '', search: '' });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [materialFiles, setMaterialFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeReason, setCloseReason] = useState('');
  const [isClosing, setIsClosing] = useState(false);

  const messagesEndRef = useRef(null);
  const lastMessageTime = useRef(null);
  const pollRef = useRef(null);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const fetchTickets = async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.service_type) params.service_type = filters.service_type;
      if (filters.search) params.search = filters.search;
      const res = await servicesService.adminListTickets(params);
      setTickets(res.data || []);
      setTotal(res.total || 0);
    } catch (err) {
      console.error('Erro ao buscar tickets:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchTickets(); }, [filters.status, filters.service_type]);
  useEffect(() => {
    const t = setTimeout(fetchTickets, 300);
    return () => clearTimeout(t);
  }, [filters.search]);

  useEffect(() => {
    if (!selectedTicket) return;
    const load = async () => {
      setIsLoadingMessages(true);
      try {
        const res = await servicesService.adminGetMessages(selectedTicket.id);
        setMessages(res.data || []);
        const msgs = res.data || [];
        lastMessageTime.current = msgs.length > 0 ? msgs[msgs.length - 1].created_at : null;
      } catch (err) { console.error(err); }
      finally { setIsLoadingMessages(false); }
    };
    load();
  }, [selectedTicket?.id]);

  useEffect(() => {
    if (!selectedTicket) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await servicesService.adminGetMessages(selectedTicket.id, lastMessageTime.current);
        const newMsgs = res.data || [];
        if (newMsgs.length > 0) {
          setMessages(prev => [...prev, ...newMsgs]);
          lastMessageTime.current = newMsgs[newMsgs.length - 1].created_at;
        }
      } catch (err) {}
    }, POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [selectedTicket?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await servicesService.adminUpdateStatus(ticketId, newStatus);
      setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, status: newStatus } : t));
      if (selectedTicket?.id === ticketId) setSelectedTicket(prev => ({ ...prev, status: newStatus }));
    } catch (err) { console.error(err); }
  };

  const handleCloseTicket = async () => {
    if (!closeReason.trim() || !selectedTicket) return;
    setIsClosing(true);
    try {
      const res = await servicesService.adminCloseTicket(selectedTicket.id, closeReason.trim());
      const updated = res.data;
      setTickets(prev => prev.map(t => t.id === updated.id ? updated : t));
      setSelectedTicket(updated);
      setShowCloseModal(false);
      setCloseReason('');
    } catch (err) { console.error(err); }
    finally { setIsClosing(false); }
  };

  const handleSendMessage = async () => {
    if (!selectedTicket || !messageText.trim()) return;
    setIsSending(true);
    try {
      const formData = new FormData();
      formData.append('content', messageText.trim());
      const res = await servicesService.adminSendMessage(selectedTicket.id, formData);
      setMessages(prev => [...prev, res.data]);
      lastMessageTime.current = res.data.created_at;
      setMessageText('');
    } catch (err) { console.error(err); }
    finally { setIsSending(false); }
  };

  const handleMaterialFileAdd = (e) => {
    const newFiles = Array.from(e.target.files || []);
    e.target.value = '';
    if (newFiles.length === 0) return;
    setMaterialFiles(prev => [...prev, ...newFiles]);
  };

  const handleMaterialUpload = async () => {
    if (!selectedTicket || materialFiles.length === 0) return;
    setIsUploading(true);
    setUploadError('');
    try {
      const formData = new FormData();
      materialFiles.forEach(file => formData.append('files', file));
      const res = await servicesService.adminSendMessage(selectedTicket.id, formData);
      if (res?.data) {
        setMessages(prev => [...prev, res.data]);
        lastMessageTime.current = res.data.created_at;
      }
      setMaterialFiles([]);
    } catch (err) {
      console.error('Erro ao enviar materiais:', err);
      const detail = err?.response?.data?.detail || err?.message || 'Falha ao enviar arquivo. Tente novamente.';
      setUploadError(typeof detail === 'string' ? detail : 'Falha ao enviar arquivo. Tente novamente.');
    }
    finally { setIsUploading(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return Image;
    if (mimeType?.startsWith('video/')) return Film;
    return FileText;
  };

  const statuses = servicesService.helpers.getStatuses();
  const serviceTypes = servicesService.helpers.getServiceTypes();
  const isClosed = selectedTicket && servicesService.helpers.isTicketClosed(selectedTicket.status);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-20 pt-4 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white/60 backdrop-blur-md rounded-3xl border border-gray-200/50 px-6 shadow-sm">
            <div className="flex items-center justify-between h-16 sm:h-20">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-11 sm:h-11 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-500">Administração</p>
                  <h1 className="text-base sm:text-lg font-bold text-gray-900">Serviços ({total})</h1>
                </div>
              </div>
              {selectedTicket && (
                <button
                  onClick={() => { setSelectedTicket(null); setMessages([]); setMaterialFiles([]); }}
                  className="lg:hidden flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                >
                  <ArrowLeft className="w-4 h-4" /> Lista
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6">
        {/* Filters - Full width bar */}
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nome ou email..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-100 focus:border-amber-400"
            />
          </div>
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="px-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-100"
          >
            <option value="">Todos os status</option>
            {statuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select
            value={filters.service_type}
            onChange={(e) => setFilters(prev => ({ ...prev, service_type: e.target.value }))}
            className="px-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-100"
          >
            <option value="">Todos os serviços</option>
            {serviceTypes.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>

        {/* Two-column layout: List + Detail */}
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6" style={{ height: 'calc(100vh - 200px)' }}>

          {/* Ticket List */}
          <div className={`${selectedTicket ? 'hidden lg:flex' : 'flex'} flex-col w-full lg:w-[400px] lg:flex-shrink-0`}>
            {/* Ticket cards */}
            <div className="flex-1 overflow-y-auto space-y-2">
              {isLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
                </div>
              ) : tickets.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <Briefcase className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Nenhum ticket encontrado</p>
                </div>
              ) : tickets.map((ticket) => (
                <div
                  key={ticket.id}
                  onClick={() => { setSelectedTicket(ticket); setMessages([]); setMaterialFiles([]); lastMessageTime.current = null; }}
                  className={`bg-white rounded-xl border p-4 cursor-pointer transition-all hover:shadow-md ${
                    selectedTicket?.id === ticket.id ? 'border-amber-400 ring-2 ring-amber-100' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <h4 className="font-semibold text-gray-900 text-sm truncate">{ticket.user_name}</h4>
                      <p className="text-xs text-gray-400 truncate">{ticket.user_email}</p>
                    </div>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${servicesService.helpers.getStatusColor(ticket.status)}`}>
                      {servicesService.helpers.getStatusLabel(ticket.status)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-gray-600">
                      {servicesService.helpers.getServiceLabel(ticket.service_type)}
                    </p>
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <Clock className="w-3 h-3" />
                      {new Date(ticket.created_at).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                  {ticket.observations && (
                    <p className="text-xs text-gray-400 line-clamp-1 mt-1">{ticket.observations}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Ticket Detail / Chat */}
          {selectedTicket ? (
            <div className="flex-1 flex flex-col bg-white rounded-2xl border border-gray-200 overflow-hidden min-h-0">
              {/* Info Header — max-h + scroll próprio: com muitos anexos ele crescia
                  sem limite, engolia o painel e travava o scroll da tela inteira */}
              <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex-shrink-0 max-h-[45%] overflow-y-auto">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg">{selectedTicket.user_name}</h3>
                    <p className="text-sm font-medium text-amber-600 mt-0.5">
                      {servicesService.helpers.getServiceLabel(selectedTicket.service_type)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {selectedTicket.status !== 'encerrado' && (
                      <select
                        value={selectedTicket.status}
                        onChange={(e) => handleStatusChange(selectedTicket.id, e.target.value)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-full border-0 cursor-pointer ${servicesService.helpers.getStatusColor(selectedTicket.status)}`}
                      >
                        {statuses.filter(s => s.value !== 'encerrado').map(s => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    )}
                    {selectedTicket.status === 'encerrado' && (
                      <span className={`px-3 py-1.5 text-xs font-medium rounded-full ${servicesService.helpers.getStatusColor('encerrado')}`}>
                        Encerrado
                      </span>
                    )}
                    {!isClosed && (
                      <button
                        onClick={() => setShowCloseModal(true)}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-full transition-colors"
                      >
                        <XCircle className="w-3 h-3" />
                        Encerrar
                      </button>
                    )}
                  </div>
                </div>

                {/* Contact info with copy buttons */}
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-3 py-1.5">
                    <Mail className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-xs text-gray-600">{selectedTicket.contact_email}</span>
                    <button
                      onClick={() => copyToClipboard(selectedTicket.contact_email, 'email')}
                      className="p-0.5 hover:bg-gray-200 rounded transition-colors"
                      title="Copiar email"
                    >
                      {copiedField === 'email' ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3 text-gray-400" />}
                    </button>
                  </div>
                  {selectedTicket.phone && (
                    <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-3 py-1.5">
                      <Phone className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-xs text-gray-600">{selectedTicket.phone}</span>
                      <button
                        onClick={() => copyToClipboard(selectedTicket.phone, 'phone')}
                        className="p-0.5 hover:bg-gray-200 rounded transition-colors"
                        title="Copiar telefone"
                      >
                        {copiedField === 'phone' ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3 text-gray-400" />}
                      </button>
                    </div>
                  )}
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(selectedTicket.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>

                {/* Observations */}
                {selectedTicket.observations && (
                  <div className="mt-3 bg-gray-50 rounded-xl px-4 py-3">
                    <p className="text-xs font-medium text-gray-500 mb-1">Observações:</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedTicket.observations}</p>
                  </div>
                )}

                {/* Close reason */}
                {selectedTicket.close_reason && (
                  <div className="mt-2 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                    <p className="text-xs font-medium text-red-600 mb-1">Motivo do encerramento:</p>
                    <p className="text-sm text-red-700">{selectedTicket.close_reason}</p>
                  </div>
                )}

                {/* Ticket attachments (grade rola sozinha quando há muitos) */}
                {selectedTicket.attachments?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2 max-h-36 overflow-y-auto">
                    {selectedTicket.attachments.map((att, idx) => (
                      <a key={idx} href={att.file_url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg px-3 py-1.5 text-xs text-gray-600 transition-colors">
                        <FileText className="w-3 h-3" />
                        <span className="truncate max-w-[120px]">{att.file_name}</span>
                        <Download className="w-3 h-3" />
                      </a>
                    ))}
                  </div>
                )}

                {/* Upload materials */}
                {!isClosed && (
                  <div className="mt-3">
                    <label className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 hover:border-gray-400 rounded-xl py-3 px-4 cursor-pointer transition-colors bg-gray-50 hover:bg-gray-100">
                      <Upload className="w-4 h-4 text-gray-400" />
                      <span className="text-xs text-gray-500">Anexar materiais</span>
                      <input type="file" multiple onChange={handleMaterialFileAdd} className="hidden" />
                    </label>
                    {materialFiles.length > 0 && (
                      <div className="mt-2 space-y-1.5">
                        {materialFiles.map((file, idx) => (
                          <div key={idx} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5 text-xs">
                            <Paperclip className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-700 truncate flex-1">{file.name}</span>
                            <button onClick={() => setMaterialFiles(prev => prev.filter((_, i) => i !== idx))}>
                              <X className="w-3 h-3 text-gray-400 hover:text-red-500" />
                            </button>
                          </div>
                        ))}
                        {uploadError && (
                          <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">
                            {uploadError}
                          </div>
                        )}
                        <button
                          onClick={handleMaterialUpload}
                          disabled={isUploading}
                          className="w-full py-2 text-xs font-medium text-white bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 rounded-xl transition-all disabled:opacity-50"
                        >
                          {isUploading ? 'Enviando...' : `Enviar ${materialFiles.length} arquivo${materialFiles.length > 1 ? 's' : ''}`}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
                {isLoadingMessages ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Nenhuma mensagem. Inicie a conversa.</p>
                  </div>
                ) : messages.map((msg) => {
                  const isAdmin = msg.sender_role === 'admin';
                  return (
                    <div key={msg.id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                      <div className="max-w-[80%] sm:max-w-[70%]">
                        <div className={`flex items-center gap-2 mb-1 ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                          {!isAdmin && <User className="w-3 h-3 text-gray-400" />}
                          <span className="text-xs font-medium text-gray-500">{msg.sender_name}</span>
                          <span className="text-xs text-gray-300">
                            {new Date(msg.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          {isAdmin && <Shield className="w-3 h-3 text-amber-500" />}
                        </div>
                        <div className={`rounded-2xl px-4 py-3 ${
                          isAdmin ? 'bg-gradient-to-r from-gray-800 to-gray-900 text-white' : 'bg-gray-100 text-gray-900'
                        }`}>
                          {msg.content && <p className="text-sm whitespace-pre-wrap">{msg.content}</p>}
                          {msg.attachments?.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {msg.attachments.map((att, idx) => {
                                const AttIcon = getFileIcon(att.mime_type);
                                return (
                                  <a key={idx} href={att.file_url} target="_blank" rel="noopener noreferrer"
                                    className={`flex items-center gap-2 text-xs rounded-lg px-3 py-2 ${
                                      isAdmin ? 'bg-white/10 text-white/80 hover:bg-white/20' : 'bg-white text-gray-600 hover:bg-gray-50'
                                    } transition-colors`}>
                                    <AttIcon className="w-4 h-4" />
                                    <span className="truncate">{att.file_name}</span>
                                    <Download className="w-3 h-3 ml-auto" />
                                  </a>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Chat Input */}
              {isClosed ? (
                <div className="border-t border-gray-200 p-4 text-center text-sm text-gray-400">
                  Ticket {selectedTicket.status === 'encerrado' ? 'encerrado' : 'concluído'}. Chat desativado.
                </div>
              ) : (
                <div className="border-t border-gray-200 p-3 sm:p-4 flex-shrink-0">
                  <div className="flex items-end gap-2">
                    <textarea
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Responder como admin..."
                      rows={1}
                      className="flex-1 px-3 sm:px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 resize-none focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400"
                      style={{ minHeight: '42px', maxHeight: '120px' }}
                      onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; }}
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={isSending || !messageText.trim()}
                      className="p-2 sm:p-2.5 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-xl hover:from-gray-700 hover:to-gray-800 transition-all disabled:opacity-40 flex-shrink-0"
                    >
                      {isSending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="hidden lg:flex flex-1 items-center justify-center bg-white rounded-2xl border border-gray-200">
              <div className="text-center text-gray-400">
                <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">Selecione um ticket</p>
                <p className="text-sm mt-1">Clique em um ticket para ver detalhes e conversar</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Close Ticket Modal */}
      {showCloseModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Encerrar Serviço</h3>
                <p className="text-sm text-gray-500">Informe o motivo do encerramento</p>
              </div>
            </div>
            <textarea
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value)}
              placeholder="Motivo do encerramento..."
              rows={3}
              maxLength={500}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 resize-none focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 mb-1"
            />
            <p className="text-xs text-gray-400 mb-4">{closeReason.length}/500</p>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowCloseModal(false); setCloseReason(''); }}
                className="flex-1 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleCloseTicket}
                disabled={!closeReason.trim() || isClosing}
                className="flex-1 py-2.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-xl transition-colors disabled:opacity-50"
              >
                {isClosing ? 'Encerrando...' : 'Encerrar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminServicesPage;
